// src/Hooks/useShippingQuery.ts
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCountries, Country } from '../Services/shippingService';

// ─────────────────────────────────────────────────────────────────────────────
// QUERY KEYS
//
// All shipping queries live under ['shipping'] so a single invalidation call
// after an admin update (clearShippingCache + invalidateQueries) covers all.
// ─────────────────────────────────────────────────────────────────────────────
export const SHIPPING_KEYS = {
  all: ['shipping'] as const,
  countries: () => ['shipping', 'countries'] as const,
};

// ─────────────────────────────────────────────────────────────────────────────
// useShippingCountries
//
// Wraps getCountries() in React Query.
//
// WHY React Query on top of the service's own cache:
//   The service already has an in-memory + localStorage cache (30 min), which
//   helps with cold loads and Firestore fallback. React Query adds:
//     - Component-level deduplication: if ShippingRates and a Checkout widget
//       both mount at the same time, only one getCountries() call fires.
//     - Automatic background refetch when the window regains focus (stale data
//       surfaced after the user switches tabs).
//     - A single invalidation point (useInvalidateShipping) that admin pages
//       can call after updating rates, without needing to know about the
//       service's internal cache variables.
//
// staleTime: 30 minutes — matches the service-layer cache duration so React
//   Query and the service stay in sync. Within this window a cached result
//   is returned synchronously with zero Firestore reads.
//
// placeholderData: (prev) => prev — keeps the previous country list visible
//   on re-mounts while a background revalidation is in flight, preventing
//   the loading spinner from flashing on return visits within the stale window.
// ─────────────────────────────────────────────────────────────────────────────
export const useShippingCountries = () => {
  return useQuery<Country[], Error>({
    queryKey: SHIPPING_KEYS.countries(),
    queryFn: getCountries,
    staleTime: 1000 * 60 * 30,          // 30 min — matches service-layer cache
    placeholderData: (prev: any) => prev, // no spinner flash on re-mount
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// useInvalidateShipping
//
// Utility for admin pages: call this after updating shipping rates in Firestore
// so the next render fetches fresh data instead of serving stale cache.
// ─────────────────────────────────────────────────────────────────────────────
export const useInvalidateShipping = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: SHIPPING_KEYS.all });
};